import i18n from "@/libraries/i18n";
import { DAYJS_LOCALE } from "@/libraries/locale-codes";
import { getCurrentLocale, useLocaleStore } from "@/stores/locale";
import dayjs, { type Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import "dayjs/locale/zh-cn";

// Extend dayjs with plugins
dayjs.extend(relativeTime);
dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

// Keeps dayjs's global locale (used by .fromNow(), .format("dddd"), etc.) in sync
// with the app locale, including on first load and every later change.
dayjs.locale(DAYJS_LOCALE[getCurrentLocale()]);
useLocaleStore.subscribe((state) => {
	dayjs.locale(DAYJS_LOCALE[state.locale]);
});

export const getHumanReadableDiff = (inputDate: string): string => {
	// Parse the input date
	const date = dayjs(inputDate);
	const now = dayjs(); // Current date and time

	// Calculate the difference dynamically
	const diffInSeconds = now.diff(date, "second");
	const diffInMinutes = now.diff(date, "minute");
	const diffInHours = now.diff(date, "hour");
	const diffInDays = now.diff(date, "day");
	const diffInYears = now.diff(date, "year");

	if (diffInSeconds < 60)
		return i18n.t("common:date.secondsAgoFull", { count: diffInSeconds });
	if (diffInMinutes < 60)
		return i18n.t("common:date.minutesAgoFull", { count: diffInMinutes });
	if (diffInHours < 24)
		return i18n.t("common:date.hoursAgoFull", { count: diffInHours });
	if (diffInDays < 365)
		return i18n.t("common:date.daysAgoFull", { count: diffInDays });
	return i18n.t("common:date.yearsAgoFull", { count: diffInYears });
};

export const readableHumanDate = (
	inputDate: string | Dayjs,
	format = "YYYY-MM-DDTHH:mm:ssZZ",
): string => {
	const date = dayjs(inputDate, format);
	return date.fromNow();
};

export const parseDate = (input: string, format?: string) => {
	const date = dayjs(input, format);
	return date;
};
